import { createDefaultProjectDocument } from '../project/defaultProjectDocument.js'
import { migrateProjectDocument } from '../project/migrateProjectDocument.js'
import {
  PROJECT_DOCUMENT_VERSION,
  type ProjectDocumentV2,
} from '../project/ProjectDocument.js'

export const PROJECT_ENVELOPE_FORMAT = 'nastaliqart-project' as const
export const PROJECT_ENVELOPE_VERSION = 1 as const

const DEFAULT_PROJECT_PREFIX = 'project:'
const MAX_PROJECT_ID_LENGTH = 256

type UnknownRecord = Record<string, unknown>

export interface ProjectKeyValueStore {
  get(key: string): Promise<unknown>
  set(key: string, value: unknown): Promise<void>
  delete(key: string): Promise<void>
  keys(): Promise<readonly string[]>
}

export interface PersistedProjectEnvelope {
  readonly format: typeof PROJECT_ENVELOPE_FORMAT
  readonly envelopeVersion: typeof PROJECT_ENVELOPE_VERSION
  readonly savedAt: number
  readonly document: ProjectDocumentV2
}

export type ProjectLoadSource =
  | 'missing'
  | 'current'
  | 'migrated'
  | 'corrupt'
  | 'unsupported'

export interface ProjectLoadResult {
  readonly document: ProjectDocumentV2
  readonly source: ProjectLoadSource
}

export interface ProjectRepositoryOptions {
  readonly prefix?: string
  readonly now?: () => number
}

interface ClassifiedRecord {
  readonly source: ProjectLoadSource
  readonly input?: unknown
  readonly needsWrite: boolean
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function hasProjectSections(value: UnknownRecord): boolean {
  return typeof value.seed === 'number'
    && isRecord(value.text)
    && isRecord(value.particles)
    && isRecord(value.appearance)
    && isRecord(value.animation)
    && isRecord(value.layout)
    && isRecord(value.canvas)
}

function isLegacyProject(value: UnknownRecord): boolean {
  return value.version === 1
    && typeof value.text === 'string'
    && isRecord(value.fontSettings)
}

function classifyDocument(value: unknown, wrapped: boolean): ClassifiedRecord {
  if (!isRecord(value)) {
    return { source: 'corrupt', needsWrite: false }
  }

  if (value.version === PROJECT_DOCUMENT_VERSION) {
    return hasProjectSections(value)
      ? { source: wrapped ? 'current' : 'migrated', input: value, needsWrite: !wrapped }
      : { source: 'corrupt', needsWrite: false }
  }

  if (value.version === 1) {
    return isLegacyProject(value)
      ? { source: 'migrated', input: value, needsWrite: true }
      : { source: 'corrupt', needsWrite: false }
  }

  if (typeof value.version === 'number' && value.version > PROJECT_DOCUMENT_VERSION) {
    return { source: 'unsupported', needsWrite: false }
  }

  return { source: 'corrupt', needsWrite: false }
}

function classifyRecord(value: unknown): ClassifiedRecord {
  if (value === undefined) {
    return { source: 'missing', needsWrite: false }
  }

  if (!isRecord(value)) {
    return { source: 'corrupt', needsWrite: false }
  }

  if (value.format !== PROJECT_ENVELOPE_FORMAT) {
    return classifyDocument(value, false)
  }

  if (typeof value.envelopeVersion === 'number'
    && value.envelopeVersion > PROJECT_ENVELOPE_VERSION) {
    return { source: 'unsupported', needsWrite: false }
  }

  if (value.envelopeVersion !== PROJECT_ENVELOPE_VERSION) {
    return { source: 'corrupt', needsWrite: false }
  }

  return classifyDocument(value.document, true)
}

function validateProjectId(id: string): string {
  if (typeof id !== 'string' || id.length === 0 || id.length > MAX_PROJECT_ID_LENGTH) {
    throw new RangeError(`Project id must contain 1-${MAX_PROJECT_ID_LENGTH} characters`)
  }
  return id
}

/**
 * Versioned, dependency-injected project persistence.
 *
 * Each project key has a small operation queue so a slow save cannot complete
 * after a newer delete. Legacy values are replaced atomically with an envelope
 * only after their V2 migration has been produced successfully.
 */
export class ProjectRepository {
  private readonly prefix: string
  private readonly now: () => number
  private readonly queues = new Map<string, Promise<void>>()

  constructor(
    private readonly storage: ProjectKeyValueStore,
    options: ProjectRepositoryOptions = {},
  ) {
    this.prefix = options.prefix ?? DEFAULT_PROJECT_PREFIX
    this.now = options.now ?? Date.now
  }

  async listIds(): Promise<string[]> {
    const keys = await this.storage.keys()
    return keys
      .filter((key) => key.startsWith(this.prefix) && key.length > this.prefix.length)
      .map((key) => key.slice(this.prefix.length))
      .sort((left, right) => left.localeCompare(right))
  }

  load(id: string): Promise<ProjectLoadResult> {
    const key = this.keyFor(id)
    return this.enqueue(key, async () => {
      const raw = await this.storage.get(key)
      let classified: ClassifiedRecord
      try {
        classified = classifyRecord(raw)
      } catch {
        classified = { source: 'corrupt', needsWrite: false }
      }
      if (classified.input === undefined) {
        return {
          document: createDefaultProjectDocument(),
          source: classified.source,
        }
      }

      let document: ProjectDocumentV2
      try {
        document = migrateProjectDocument(classified.input)
      } catch {
        return {
          document: createDefaultProjectDocument(),
          source: 'corrupt',
        }
      }
      if (classified.needsWrite) {
        await this.storage.set(key, this.envelope(document))
      }
      return { document, source: classified.source }
    })
  }

  save(id: string, input: unknown): Promise<void> {
    const key = this.keyFor(id)
    // Snapshot synchronously so caller mutation after save() cannot change the write.
    const envelope = this.envelope(migrateProjectDocument(input))
    return this.enqueue(key, () => this.storage.set(key, envelope))
  }

  delete(id: string): Promise<void> {
    const key = this.keyFor(id)
    return this.enqueue(key, () => this.storage.delete(key))
  }

  private keyFor(id: string): string {
    return `${this.prefix}${validateProjectId(id)}`
  }

  private envelope(document: ProjectDocumentV2): PersistedProjectEnvelope {
    return {
      format: PROJECT_ENVELOPE_FORMAT,
      envelopeVersion: PROJECT_ENVELOPE_VERSION,
      savedAt: this.now(),
      document,
    }
  }

  private enqueue<TResult>(key: string, operation: () => Promise<TResult>): Promise<TResult> {
    const previous = this.queues.get(key) ?? Promise.resolve()
    const result = previous.catch(() => undefined).then(operation)
    const tail = result.then(
      () => undefined,
      () => undefined,
    )
    this.queues.set(key, tail)
    void tail.then(() => {
      if (this.queues.get(key) === tail) this.queues.delete(key)
    })
    return result
  }
}
